import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftRight, Plus, Minus, Calendar, X } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TravelAutocompleteInput from "../search-form/TravelAutocompleteInput";
import { toast } from 'react-toastify';

/**
 * TripSearchSection — "Create a Trip" (beta)
 *
 * Mirrors FormSearchSection's structure & CSS classes so it blends with the
 * existing home styles (search-bar, home-search-bar, search-field-input,
 * TravelAutocompleteInput + location tag pattern, react-datepicker,
 * primary-search-btn).
 *
 * Both the free-text search and the form navigate to /trip-planner with an
 * initiator sentence in location.state:
 *   { fromHero: true, initiator: "<text or composed sentence>" }
 *
 * Form sentence format:
 *   "Create a trip for {duration} days from {from} to {to} starting on {dd-mm-yyyy}"
 */
const TripSearchSection = () => {
    const navigate = useNavigate();

    const tomorrow = () => new Date(Date.now() + 86400000);

    const [tripText, setTripText] = useState("");
    const [fromLocation, setFromLocation] = useState("");
    const [toLocation, setToLocation] = useState("");
    const [fromLocationData, setFromLocationData] = useState(null);
    const [toLocationData, setToLocationData] = useState(null);
    const [travelDate, setTravelDate] = useState(tomorrow()); // default: tomorrow
    const [tripDays, setTripDays] = useState(3);              // default duration: 3 days

    const isFormValid = fromLocation.trim() && toLocation.trim() && travelDate;

    // Date → dd-mm-yyyy (the format the trip-planner intake parses reliably)
    const toDMY = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
    };

    // Prefer the clean city name for the trip sentence:
    // autocomplete item.city if selected, else strip "(CODE)" from typed text
    const cityOf = (data, text) =>
        (data?.city || String(text).split('(')[0]).trim();

    /* Free-text trip search → raw text as initiator */
    const handleTripTextSearch = (e) => {
        e.preventDefault();
        if (!tripText.trim()) return;
        navigate("/trip-planner", { state: { fromHero: true, initiator: tripText.trim() } });
    };

    /* Form search → compose the sentence */
    const handleCreateTrip = () => {
        if (!fromLocation.trim()) {
            toast.error('Please enter departure city', { position: 'top-center' });
            return;
        }
        if (!toLocation.trim()) {
            toast.error('Please enter destination city', { position: 'top-center' });
            return;
        }
        if (!travelDate) {
            toast.error('Please select start date', { position: 'top-center' });
            return;
        }

        const fromCity = cityOf(fromLocationData, fromLocation);
        const toCity = cityOf(toLocationData, toLocation);

        if (fromCity.toLowerCase() === toCity.toLowerCase()) {
            toast.error('Origin and Destination cannot be the same', { position: 'top-center' });
            return;
        }

        const sentence = `Create a trip for ${tripDays} day${tripDays !== 1 ? "s" : ""} from ${fromCity} to ${toCity} starting on ${toDMY(travelDate)}`;
        navigate("/trip-planner", { state: { fromHero: true, initiator: sentence } });
    };

    return (
        <section className="home-search-section">
            {/* AI Trip Search Bar — same look as the routes AI bar */}
            <div className="search-bar" style={{ margin: 0 }}>
                <form onSubmit={handleTripTextSearch} style={{ display: "flex", width: "100%", alignItems: "center" }}>
                    <div className="search-input" style={{ flex: 1 }}>
                        <img src="/Star1.svg" alt="AI" />
                        <input
                            type="text"
                            value={tripText}
                            onChange={e => setTripText(e.target.value)}
                            placeholder="Plan a 3 day trip to Chikmagalur from Bengaluru starting tomorrow"
                            style={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                width: '100%'
                            }}
                        />
                    </div>
                    <button className="search-button" type="submit">
                        <img src="/send.svg" alt="Send" />
                    </button>
                </form>
            </div>

            {/* Divider — identical markup to FormSearchSection */}
            <div className="form-divider-or">
                <div className="divider-line"></div>
                OR
                <div className="divider-line"></div>
            </div>

            {/* Trip Form: From · To · Start date · Duration */}
            <div className="form-search-container">
                <div className="home-search-bar-wrapper" style={{ width: '100%', position: 'relative' }}>
                    <div className="home-search-bar">
                        <div className="search-field home-search-main-field">
                            <div className="home-input-wrapper">
                                <span className="mobile-label">From</span>
                                {fromLocationData ? (
                                    <div className="home-location-tag">
                                        <span className="home-location-name">{fromLocation.split('(')[0].trim()}</span>
                                        <button className="home-tag-clear-btn" onClick={() => {
                                            setFromLocation("");
                                            setFromLocationData(null);
                                        }}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <TravelAutocompleteInput
                                        className="search-field-input"
                                        placeholder="From"
                                        value={fromLocation}
                                        onChange={(val) => {
                                            setFromLocation(val);
                                            setFromLocationData(null);
                                        }}
                                        onSelect={(item) => {
                                            setFromLocation(item.label);
                                            setFromLocationData(item);
                                        }}
                                    />
                                )}
                            </div>

                            <div className="swap-btn" onClick={() => {
                                const temp = fromLocation;
                                const tempData = fromLocationData;
                                setFromLocation(toLocation);
                                setFromLocationData(toLocationData);
                                setToLocation(temp);
                                setToLocationData(tempData);
                            }}>
                                <ArrowLeftRight size={20} />
                            </div>

                            <div className="home-input-wrapper">
                                <span className="mobile-label">To</span>
                                {toLocationData ? (
                                    <div className="home-location-tag">
                                        <span className="home-location-name">{toLocation.split('(')[0].trim()}</span>
                                        <button className="home-tag-clear-btn" onClick={() => {
                                            setToLocation("");
                                            setToLocationData(null);
                                        }}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <TravelAutocompleteInput
                                        className="search-field-input"
                                        placeholder="To"
                                        value={toLocation}
                                        onChange={(val) => {
                                            setToLocation(val);
                                            setToLocationData(null);
                                        }}
                                        onSelect={(item) => {
                                            setToLocation(item.label);
                                            setToLocationData(item);
                                        }}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="search-field-sep"></div>

                        <div className="search-field date-picker-container">
                            <span className="mobile-label">Start date</span>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', cursor: 'pointer' }}>
                                <DatePicker
                                    selected={travelDate}
                                    onChange={(date) => setTravelDate(date)}
                                    dateFormat="MMM dd/eee"
                                    className="search-field-input"
                                    placeholderText="Start date"
                                    minDate={new Date()}
                                    isClearable={false}
                                    popperPlacement="bottom"
                                />
                                <Calendar size={18} color="#64748B" className="mobile-only-icon" style={{ flexShrink: 0 }} />
                            </div>
                        </div>

                        <div className="search-field-sep"></div>

                        {/* Duration — stepper, defaults to 3 days */}
                        <div className="search-field" style={{ flex: '0 0 auto' }}>
                            <span className="mobile-label">Duration</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px' }}>
                                <button
                                    type="button"
                                    className="count-btn"
                                    aria-label="Fewer days"
                                    onClick={() => setTripDays(d => Math.max(1, d - 1))}
                                >
                                    <Minus size={14} />
                                </button>
                                <span style={{
                                    fontFamily: "'Inter', sans-serif", fontWeight: 600,
                                    fontSize: 18, color: '#0A0A0A',
                                    minWidth: 72, textAlign: 'center', whiteSpace: 'nowrap',
                                }}>
                                    {tripDays} day{tripDays !== 1 ? "s" : ""}
                                </span>
                                <button
                                    type="button"
                                    className="count-btn"
                                    aria-label="More days"
                                    onClick={() => setTripDays(d => Math.min(15, d + 1))}
                                >
                                    <Plus size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modes-container">
                    <span style={{ display: "block", textAlign: "center", lineHeight: 1.6 }}>
                        Note: hotel bookings are under testing.
                    </span>
                    <button
                        className={`primary-search-btn ${!isFormValid ? 'disabled' : ''}`}
                        disabled={!isFormValid}
                        onClick={handleCreateTrip}
                        style={{ width: "auto", minWidth: 200, padding: "8px 36px" }}
                    >
                        Create Trip ✨
                    </button>
                </div>
            </div>

        </section>
    );
};

export default TripSearchSection;
